import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { mapError } from '../../lib/errors';
import { isDisposableEmail } from '../../lib/emailValidator';
import { PasswordField, isPasswordValid } from './PasswordField';
import { PasswordStrength } from './PasswordStrength';
import { OAuthButtons, OAuthDivider } from './OAuthButtons';
import { Spinner } from '../ui/Spinner';
import { useI18n } from '../../hooks/useI18n';
import { Link } from 'react-router-dom';
import { trackOnboardingEvent } from '../../lib/analytics-events';

interface RegisterStep1Props {
  onComplete: (userId: string, email: string) => void;
}

export function RegisterStep1({ onComplete }: RegisterStep1Props) {
  const { t }          = useI18n();
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [passwordOk,   setPasswordOk]   = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'apple' | null>(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  const canSubmit = email.includes('@') && passwordOk && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isPasswordValid(password)) return;
    if (isDisposableEmail(email)) {
      setError('Por favor, use um email permanente para criar sua conta.');
      return;
    }

    setLoading(true);
    setError(null);
    trackOnboardingEvent('step1_email_completed', { email });

    const { data, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/onboarding` },
    });

    if (authError) { setError(mapError(authError)); setLoading(false); return; }
    if (!data.user) { setError('Falha ao criar conta. Tente novamente.'); setLoading(false); return; }

    onComplete(data.user.id, email);
  }

  async function handleGoogle() {
    trackOnboardingEvent('register_cta_clicked', { metadata: { method: 'google' } });
    setOauthLoading('google');
    const { error: oErr } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/onboarding` },
    });
    if (oErr) { setError(t('auth.oauthError')); setOauthLoading(null); }
  }

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-28)', color: 'var(--bg)', margin: '0 0 0.35rem' }}>
          Comece seu mapa natal
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
          Em 30 segundos você terá seu mapa completo
        </p>
      </div>


      <OAuthButtons onGoogle={handleGoogle} loadingProvider={oauthLoading} labelGoogle="Continuar com Google" />
      <OAuthDivider label="ou entre com e-mail" />

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label htmlFor="reg-email" style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Email
          </label>
          <input
            id="reg-email"
            type="email"
            className="form-input"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="seu@email.com"
            autoComplete="email"
            required
            style={{ width: '100%' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Senha
          </label>
          <PasswordField value={password} onChange={setPassword} onValidChange={setPasswordOk} />
          <PasswordStrength password={password} />
        </div>

        {error && (
          <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', margin: 0 }}>⚠ {error}</p>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={!canSubmit}
          style={{ marginTop: '0.25rem' }}
        >
          {loading ? <Spinner size="sm" /> : 'Continuar →'}
        </button>

        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
          Ao continuar, você concorda com os{' '}
          <Link to="/termos" style={{ color: 'var(--petrol)' }}>Termos de Uso</Link>
          {' '}e{' '}
          <Link to="/privacidade" style={{ color: 'var(--petrol)' }}>Política de Privacidade</Link>.
        </p>

        <p style={{ textAlign: 'center', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: 0 }}>
          Já tem conta?{' '}
          <Link to="/login" style={{ color: 'var(--petrol)', fontWeight: 600 }}>Entrar</Link>
        </p>
      </form>
    </>
  );
}
